import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { RotateCcw } from "lucide-react";
import { TextArea } from "@/components/TextArea";
import { useForm, Controller } from "react-hook-form";
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useReopenOccurrence } from "@/hooks/useOccurrences";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

type ReopenOccurrenceDialogProps = {
  showDialog: boolean;
  setShowDialog: (open: boolean) => void;
  occurrenceId: string;
};

const reopenReasons = [
  { value: "NOT_SOLVED", label: "Problema não foi resolvido" },
  { value: "PARTIALLY_SOLVED", label: "Problema resolvido parcialmente" },
  { value: "PROBLEM_RECURRED", label: "Problema voltou a ocorrer" },
  {
    value: "POOR_QUALITY_SERVICE",
    label: "Serviço executado com má qualidade",
  },
  { value: "OTHER", label: "Outro" },
];

const reopenOccurrenceSchema = z.object({
  reason: z.string().min(1, "Selecione um motivo"),
  description: z
    .string()
    .min(20, "A descrição deve ter no mínimo 20 caracteres")
    .max(500, "A descrição deve ter no máximo 500 caracteres"),
});

type ReopenOccurrenceFormData = z.infer<typeof reopenOccurrenceSchema>;

export const ReopenOccurrenceDialog = (props: ReopenOccurrenceDialogProps) => {
  const navigate = useNavigate();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formDataToSubmit, setFormDataToSubmit] =
    useState<ReopenOccurrenceFormData | null>(null);
  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors },
  } = useForm<ReopenOccurrenceFormData>({
    resolver: zodResolver(reopenOccurrenceSchema),
    defaultValues: {
      reason: "",
      description: "",
    },
  });
  const reopenOccurrence = useReopenOccurrence();

  const handleClose = () => {
    reset();
    setShowConfirmation(false);
    props.setShowDialog(false);
  };

  const submitReopenOccurrence = (data: ReopenOccurrenceFormData) => {
    setFormDataToSubmit(data);
    setShowConfirmation(true);
  };

  const confirmReopenOccurrence = async () => {
    if (!formDataToSubmit) return;

    await reopenOccurrence.mutateAsync(
      {
        id: props.occurrenceId,
        reason: formDataToSubmit.reason as any,
        description: formDataToSubmit.description,
      },
      {
        onSuccess: () => {
          toast.success("Ocorrência reaberta com sucesso!");
          handleClose();
          navigate("/occurrences");
        },
        onError: (error: any) => {
          toast.error(
            error?.response?.data?.message ||
              "Erro ao reabrir ocorrência. Tente novamente.",
          );
          setShowConfirmation(false);
        },
      },
    );
  };

  return (
    <>
      <Dialog open={props.showDialog} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[600px]">
          <form onSubmit={handleSubmit(submitReopenOccurrence)}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <div className="p-2 bg-[hsl(var(--secondary))] rounded-lg">
                  <RotateCcw className="h-5 w-5 text-white" />
                </div>
                Reabrir Ocorrência
              </DialogTitle>
              <DialogDescription>
                Informe o motivo para reabrir esta ocorrência.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Motivo da Reabertura <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="reason"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger
                        className={errors.reason ? "border-red-500" : ""}
                      >
                        <SelectValue placeholder="Selecione o motivo" />
                      </SelectTrigger>
                      <SelectContent>
                        {reopenReasons.map((reason) => (
                          <SelectItem key={reason.value} value={reason.value}>
                            {reason.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.reason && (
                  <p className="text-sm text-red-500">
                    {errors.reason.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Descrição <span className="text-red-500">*</span>
                </label>
                <TextArea
                  {...register("description")}
                  placeholder="Descreva detalhadamente o motivo da reabertura..."
                  className={errors.description ? "border-red-500" : ""}
                  rows={5}
                />
                {errors.description && (
                  <p className="text-sm text-red-500">
                    {errors.description.message}
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={reopenOccurrence.isPending}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="text-white cursor-pointer bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))]"
                disabled={reopenOccurrence.isPending}
              >
                Reabrir Ocorrência
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Reabertura</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja reabrir esta ocorrência? Esta ação irá
              iniciar um novo ciclo de atendimento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reopenOccurrence.isPending}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmReopenOccurrence}
              disabled={reopenOccurrence.isPending}
              className="text-white cursor-pointer bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))]"
            >
              {reopenOccurrence.isPending
                ? "Reabrindo..."
                : "Confirmar Reabertura"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
